import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import {
  Accordion,
  ItemGrid,
} from "components/DesignSystem/Accordion/Accordion";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Breadcrumb from "components/DesignSystem/Breadcrumb/Breadcrumb";
import { Chip } from "components/DesignSystem/Chips/Chips";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Link } from "components/DesignSystem/Link/Link";
import Slider from "components/DesignSystem/Slider/Slider";
import Tab from "components/DesignSystem/Tab/Tab";
import { Tag } from "components/DesignSystem/Tag/Tag";
import dayjs from "dayjs";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { UpdateFilterFunction, useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { createContext, useContext, useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  BillDetailsResponse,
  useGetExpenseScheduleBillDetailsQuery,
  useGetExpenseScheduleVendorDetailsQuery,
  VendorDetailsResponse,
} from "store/apis/expenseSchedule";
import { useGetInfiniteAllTransactionMerchantsInfiniteQuery } from "store/apis/merchant";
import { Merchant } from "types/Models/merchant";
import { debounce } from "utils/debouncing";
import { ArrayElement } from "types/utils/ArrayElement";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter";
import {
  RECOGNITION_STATUS,
  RecognitionStatus,
} from "constants/deferredRecognition";

const ExpenseScheduleContext = createContext<{
  header: string;
  billStartDate?: string;
  billEndDate?: string;
  recognitionStartDate?: string;
  recognitionEndDate?: string;
  recognitionType?: RecognitionStatus;
}>({
  header: "",
  billStartDate: "",
  billEndDate: "",
  recognitionStartDate: "",
  recognitionEndDate: "",
  recognitionType: undefined,
});

type PossibleFilters = {
  vendors: string[];
};

type FilterProps = {
  updateFilter: UpdateFilterFunction<PossibleFilters>;
  filters: PossibleFilters;
};

const Filters = ({ filters, updateFilter }: FilterProps) => {
  const groupId = useCurrentGroupContext().uuid;
  const entityId = useCurrentEntityId();
  const [vendorSearchValue, setVendorSearchValue] = useState("");

  const {
    data: vendorsData,
    fetchNextPage,
    hasNextPage,
  } = useGetInfiniteAllTransactionMerchantsInfiniteQuery({
    groupId,
    entityId,
    search_term: vendorSearchValue,
  });

  const vendors = useMemo(() => {
    return (
      vendorsData?.pages?.reduce((acc, page) => {
        return [...acc, ...(page.merchants || [])];
      }, [] as Merchant[]) || []
    );
  }, [vendorsData]);
  const onVendorSearch = debounce((value: string) =>
    setVendorSearchValue(value)
  );

  return (
    <Filter.Root
      defaultValue="VENDORS"
      capsule={
        filters.vendors.length > 0 && (
          <>
            {filters?.vendors?.map((vendorId) => (
              <Chip
                isActive
                filterType="VENDORS"
                onRemove={() =>
                  updateFilter(
                    "vendors",
                    filters.vendors.filter((id) => id !== vendorId)
                  )
                }
                key={vendorId}
              >
                {vendors?.find((vendor) => vendor.uuid === vendorId)?.name}
              </Chip>
            ))}
          </>
        )
      }
    >
      <Filter.Portal size="small">
        <Filter.List size="small">
          <Filter.ListItem value="VENDORS">Vendors</Filter.ListItem>
        </Filter.List>
        <Filter.Body value="VENDORS" block>
          <MultiSelectFilter
            onSearchValueChange={onVendorSearch}
            hasMore={hasNextPage}
            totalCount={vendors.length}
            loadNext={fetchNextPage}
            selected={filters.vendors}
            onChange={(newValues) => updateFilter("vendors", newValues)}
            options={vendors.map((c) => ({
              label: c.name,
              value: c.uuid,
            }))}
          />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};

const VendorDetailsTable = ({
  onVendorSelect,
  values,
  updateFilter,
}: {
  onVendorSelect: (vendorId: string) => void;
  values: FilterProps["filters"];
  updateFilter: FilterProps["updateFilter"];
}) => {
  const {
    billEndDate,
    billStartDate,
    recognitionType,
    recognitionEndDate,
    recognitionStartDate,
  } = useContext(ExpenseScheduleContext);

  const entityId = useCurrentEntityId();
  const { data, isLoading, isSuccess } =
    useGetExpenseScheduleVendorDetailsQuery(
      {
        entityId: entityId,
        vendor_ids: values.vendors.join(","),
        bill_date_end: billEndDate,
        bill_date_start: billStartDate,
        recognition_type: recognitionType,
        recognition_date_end: recognitionEndDate,
        recognition_date_start: recognitionStartDate,
      },
      {
        skip: !entityId,
      }
    );

  const columnHelper =
    createColumnHelper<ArrayElement<VendorDetailsResponse>>();

  const columns = [
    columnHelper.accessor("vendor_name", {
      header: "Name",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("total_amount", {
      header: () => <div className="t-text-right">Amount</div>,
      cell: (info) => (
        <div className="t-text-right">
          <AmountSuperScript amount={Number(info.getValue())} />{" "}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <Filters filters={values} updateFilter={updateFilter} />
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={data?.length === 0}
      >
        <Async.Empty>
          <div className="t-text-center t-py-10 t-text-text-30">
            No vendors found
          </div>
        </Async.Empty>
        <Async.Success>
          <TableUI
            size="regular"
            sticky={false}
            table={table}
            onRowClick={(row) => onVendorSelect(row.original.merchant_id)}
          />
        </Async.Success>
      </Async.Root>
    </div>
  );
};

const BillItemDetails = ({
  header,
  scheduleDetails,
}: {
  header: string;
  scheduleDetails: ArrayElement<BillDetailsResponse>;
}) => {
  const columnHelper =
    createColumnHelper<(typeof scheduleDetails.expense_schedule)[0]>();

  const columns = [
    columnHelper.accessor("period", {
      header: "Period",
      cell: (info) => info.getValue(),
      size: 25,
    }),

    columnHelper.accessor("amount", {
      header: () => <div className="t-text-right">Recognition Amount</div>,
      cell: (info) => (
        <div className="t-text-right">
          <AmountSuperScript amount={Number(info.getValue())} />{" "}
        </div>
      ),
      size: 40,
    }),

    columnHelper.accessor("status", {
      header: () => <div className="t-text-right">Status</div>,
      cell: (info) => (
        <div className="t-text-right">
          <Tag
            transparent
            tagType={
              info.getValue() === RECOGNITION_STATUS.RECOGNIZED
                ? "green"
                : "yellow"
            }
          >
            {capitalizeFirstLetter(info.getValue().toLowerCase())}
          </Tag>
        </div>
      ),
      size: 35,
    }),
  ];

  const table = useReactTable({
    data: scheduleDetails.expense_schedule,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { link } = useConstructInternalLink();

  const {
    item_name,
    bill_number,
    booked_amount,
    bill_id,
    vendor_name: vendorName,
  } = scheduleDetails;

  return (
    <>
      <Slider.Header>
        <Slider.Title>
          <div className="t-flex t-flex-col t-gap-1">
            <Breadcrumb.Root>
              <Slider.RawClose asChild>
                <Breadcrumb.Item as="button">{header}</Breadcrumb.Item>
              </Slider.RawClose>
            </Breadcrumb.Root>
            {item_name} ({bill_number})
          </div>
        </Slider.Title>
        <Slider.Close />
      </Slider.Header>
      <Slider.Body className="t-flex t-flex-col t-gap-4 t-text-text-60">
        <Accordion.Root defaultValue={"item-details"} type="single" collapsible>
          <Accordion.Item value="item-details">
            <Accordion.Trigger disabled>
              <InfoItem label="Amount Booked">
                <AmountSuperScript amount={Number(booked_amount || 0)} />
              </InfoItem>
            </Accordion.Trigger>
            <Accordion.Content>
              <ItemGrid>
                <InfoItem label="Vendor">{vendorName}</InfoItem>
                <InfoItem label="Category">Income</InfoItem>
                <InfoItem label="Recognition Period">
                  {dayjs(
                    scheduleDetails.expense_schedule.at(0)?.period_start
                  ).format("MMM 'YY")}{" "}
                  -{" "}
                  {dayjs(
                    scheduleDetails.expense_schedule.at(-1)?.period_end
                  ).format("MMM 'YY")}
                </InfoItem>
                <InfoItem label="Linked Bill">
                  <Link to={link(`/books/bills-and-vendors/bill/${bill_id}`)}>
                    {bill_number}
                  </Link>
                </InfoItem>
              </ItemGrid>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
        <TableUI table={table} size="regular" />
      </Slider.Body>
    </>
  );
};

const BillDetailsTable = ({
  values,
  updateFilter,
  header,
}: {
  values: FilterProps["filters"];
  updateFilter: FilterProps["updateFilter"];
  header: string;
}) => {
  const {
    billEndDate,
    billStartDate,
    recognitionType,
    recognitionEndDate,
    recognitionStartDate,
  } = useContext(ExpenseScheduleContext);

  const entityId = useCurrentEntityId();
  const { data, isLoading, isSuccess } = useGetExpenseScheduleBillDetailsQuery(
    {
      entityId: entityId,
      vendor_ids: values.vendors.join(","),
      bill_date_end: billEndDate,
      bill_date_start: billStartDate,
      recognition_type: recognitionType,
      recognition_date_end: recognitionEndDate,
      recognition_date_start: recognitionStartDate,
    },
    {
      skip: !entityId,
    }
  );

  const columnHelper = createColumnHelper<ArrayElement<BillDetailsResponse>>();

  const [billSelected, setBillSelected] = useState<string | null>(null);

  const columns = [
    columnHelper.accessor("item_name", {
      header: "Name",
      cell: (info) => {
        return (
          <div className="t-flex t-flex-col t-gap-0.5">
            <span className="t-text-body-sm">{info.getValue()}</span>
            <span className="t-text-body-sm t-text-text-30">
              {info.row.original.bill_number}
            </span>
          </div>
        );
      },
      size: 30,
    }),
    columnHelper.accessor("vendor_name", {
      header: "Vendor",
      cell: (info) => info.getValue(),
      size: 30,
    }),
    columnHelper.accessor("amount", {
      header: () => <div className="t-text-right">Recognised Amount</div>,
      cell: (info) => (
        <div className="t-text-right">
          <AmountSuperScript amount={Number(info.getValue())} />{" "}
        </div>
      ),
      size: 40,
    }),
  ];

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const selectedBillItem = data?.find((item) => item.item_id === billSelected);

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <Filters filters={values} updateFilter={updateFilter} />
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={data?.length === 0}
      >
        <Async.Empty>
          <div className="t-text-center t-py-10 t-text-text-30">
            No bills found
          </div>
        </Async.Empty>
        <Async.Success>
          <TableUI
            sticky={false}
            table={table}
            onRowClick={(row) => setBillSelected(row.original.item_id)}
          />
        </Async.Success>
      </Async.Root>
      <Slider.Root
        open={Boolean(billSelected)}
        onOpenChange={(open) => !open && setBillSelected(null)}
      >
        <Slider.Content>
          {data && selectedBillItem && (
            <BillItemDetails
              scheduleDetails={selectedBillItem}
              header={header}
            />
          )}
        </Slider.Content>
      </Slider.Root>
    </div>
  );
};

const Content = ({ header }: { header: string }) => {
  const [currentTab, setCurrentTab] = useState<"BILL" | "VENDOR">("VENDOR");
  const { values, updateFilter } = useFilters({
    initialValue: {
      vendors: [] as string[],
    },
  });
  const handleVendorSelect = (vendorId: string) => {
    updateFilter("vendors", [vendorId]);
    setCurrentTab("BILL");
  };

  return (
    <Tab.Root
      value={currentTab}
      onValueChange={(e) => setCurrentTab(e as typeof currentTab)}
    >
      <Slider.Header
        bottom={
          <Tab.List>
            <Tab.Trigger value="VENDOR">Vendors</Tab.Trigger>
            <Tab.Trigger value="BILL">Bills</Tab.Trigger>
          </Tab.List>
        }
      >
        <Slider.Title>{header}</Slider.Title>
        <Slider.Close />
      </Slider.Header>
      <Slider.Body>
        <Tab.Content value="VENDOR">
          <VendorDetailsTable
            values={values}
            updateFilter={updateFilter}
            onVendorSelect={handleVendorSelect}
          />
        </Tab.Content>
        <Tab.Content value="BILL">
          <BillDetailsTable
            header={header}
            values={values}
            updateFilter={updateFilter}
          />
        </Tab.Content>
      </Slider.Body>
    </Tab.Root>
  );
};

export const ExpenseWaterfallDetailsModal = ({
  header,
  children,
  billStartDate,
  billEndDate,
  recognitionStartDate,
  recognitionEndDate,
  recognitionType,
  onOpen,
  cell_id,
}: {
  children: React.ReactNode;
  header: string;
  billStartDate?: string;
  billEndDate?: string;
  recognitionStartDate?: string;
  recognitionEndDate?: string;
  recognitionType?: RecognitionStatus;
  onOpen: (open: boolean, cell_id: string) => void;
  cell_id: string;
}) => {
  const value = useMemo(
    () => ({
      header,
      billStartDate,
      billEndDate,
      recognitionStartDate,
      recognitionEndDate,
      recognitionType,
    }),
    [
      header,
      billStartDate,
      billEndDate,
      recognitionStartDate,
      recognitionEndDate,
      recognitionType,
    ]
  );

  const slider = useModal();

  const handleOpenChange = (open: boolean) => {
    slider.toggle();
    onOpen(open, cell_id);
  };

  return (
    <ExpenseScheduleContext.Provider value={value}>
      <Slider.Root
        onOpenChange={handleOpenChange}
        open={slider.isOpen}
        modal={false}
      >
        <Slider.Trigger asChild>{children}</Slider.Trigger>
        <Slider.Content className="t-h-full t-overflow-scroll" useCustomOverlay>
          <Content header={header} />
        </Slider.Content>
      </Slider.Root>
    </ExpenseScheduleContext.Provider>
  );
};
